import React, { useCallback, useMemo } from 'react';
import dayjs from 'src/utils/dayjs';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { Box, Button, TextField } from '@mui/material';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import DropdownMenu from 'src/components/DropdownMenu';
import {
  filterAllowedGracePeriods,
  GracePeriodValues
} from 'src/utils/constants/distributors';
import { selectDistributorDetail } from 'src/store/selectors/distributorSelectors';
import { useSelector } from 'react-redux';
import {
  calculateEndDateFromGracePeriod,
  calculateFirstRentDateFromGracePeriod,
  calculateSecondRentDateFromGracePeriod,
  shouldDisableGracePeriod
} from 'src/utils/contracts';
import Autocomplete from 'src/components/Autocomplete/Autocomplete';
import momentLocalTimezone from 'src/utils/momentLocalTimezone';
import generateNumbersList from 'src/utils/generateNumbersList';
import { getMaxDateForSecondRentDate } from 'src/utils/contract';
import useStyles from '../styles';
import validationSchema from './schema';
import makeInitialState from './initialState';

const StepFour = ({ data, handleNext, handleBack, resetRef }) => {
  const { classes } = useStyles();
  const parameter = useSelector(selectDistributorDetail);
  const initialState = useMemo(() => makeInitialState(data, parameter), [data]);
  const { t } = useTranslation();
  const setDatesOnSignatureDateChange = (setFieldValue, values, newValue) => {
    setFieldValue('startDate', dayjs(newValue));

    const firstRentDate = calculateFirstRentDateFromGracePeriod(
      momentLocalTimezone(newValue),
      values.gracePeriod,
      values.isFirstUpfrontRentModality,
      Number(values.initialPayment)
    );

    setFieldValue('firstRentDate', firstRentDate);

    if (values.isFirstUpfrontRentModality) {
      setFieldValue('secondRentDate', firstRentDate.clone().add(1, 'month'));
    }

    setFieldValue(
      'endDate',
      calculateEndDateFromGracePeriod(
        momentLocalTimezone(newValue),
        values.term,
        values.gracePeriod,
        values.isFirstUpfrontRentModality
      )
    );
  };

  const onNext = useCallback(
    values => {
      handleNext(values);
    },
    [handleNext]
  );

  return (
    <Formik
      initialValues={initialState}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={onNext}
    >
      {({
        errors,
        handleSubmit,
        touched,
        values,
        setFieldValue,
        resetForm,
        handleBlur
      }) => {
        resetRef.current = () => resetForm();
        return (
          <form onSubmit={handleSubmit}>
            <Box className={classes.container}>
              <DatePicker
                label={t('CONTRACT_VIEW.SIGNATURE_DATE')}
                format="DD/MM/YYYY"
                value={values.startDate}
                onChange={newValue => {
                  setDatesOnSignatureDateChange(
                    setFieldValue,
                    values,
                    newValue
                  );
                }}
                slotProps={{
                  textField: {
                    sx: { width: '100%', margin: '16px 0' },
                    error: Boolean(touched.startDate && errors.startDate),
                    helperText: touched.startDate && t(errors.startDate),
                    FormHelperTextProps: { sx: { color: 'red' } },
                    InputProps: { autoComplete: 'off' }
                  }
                }}
              />
              {!data.isFirstUpfrontRentModality && (
                <DatePicker
                  label={t('CONTRACT_VIEW.FIRST_RENT_DATE')}
                  value={values.firstRentDate}
                  disabled
                  format="DD/MM/YYYY"
                  onChange={newValue => {
                    setFieldValue('firstRentDate', dayjs(newValue));
                  }}
                  slotProps={{
                    textField: {
                      sx: { width: '100%', margin: '16px 0' },
                      error: Boolean(touched.firstRentDate && errors.firstRentDate),
                      helperText: touched.firstRentDate && t(errors.firstRentDate),
                      FormHelperTextProps: { sx: { color: 'red' } },
                      InputProps: { autoComplete: 'off' }
                    }
                  }}
                />
              )}
              {data.isFirstUpfrontRentModality && (
                <DatePicker
                  label={t('CONTRACT_VIEW.SECOND_RENT_DATE')}
                  value={values.secondRentDate}
                  format="DD/MM/YYYY"
                  maxDate={getMaxDateForSecondRentDate(
                    parameter.maxGracePeriod,
                    values.startDate
                  )}
                  minDate={momentLocalTimezone(values.startDate).add(
                    2,
                    'months'
                  )}
                  onChange={newValue => {
                    setFieldValue(
                      'secondRentDate',
                      momentLocalTimezone(newValue)
                    );
                    setFieldValue(
                      'endDate',
                      momentLocalTimezone(newValue).add(data.term - 2, 'months')
                    );
                  }}
                  disabled={
                    Number(parameter.maxGracePeriod) <=
                      GracePeriodValues.THIRTY ||
                    data.isFirstUpfrontRentModality
                  }
                  slotProps={{
                    textField: {
                      sx: { width: '100%', margin: '16px 0' },
                      error: Boolean(touched.secondRentDate && errors.secondRentDate),
                      helperText: touched.secondRentDate && t(errors.secondRentDate),
                      FormHelperTextProps: { sx: { color: 'red' } },
                      InputProps: { autoComplete: 'off' }
                    }
                  }}
                />
              )}
              <DropdownMenu
                list={filterAllowedGracePeriods(
                  parameter.maxGracePeriod,
                  values.isFirstUpfrontRentModality
                )}
                onBlur={handleBlur}
                label={t('COMMON.DAYS_FOR_FIRST_RENT')}
                value={values.gracePeriod}
                name="gracePeriod"
                setValue={value => {
                  setFieldValue('gracePeriod', value);
                  setFieldValue(
                    'firstRentDate',
                    calculateFirstRentDateFromGracePeriod(
                      momentLocalTimezone(values.startDate),
                      value,
                      data.isFirstUpfrontRentModality,
                      Number(values.initialPayment)
                    )
                  );
                  setFieldValue(
                    'secondRentDate',
                    calculateSecondRentDateFromGracePeriod(
                      momentLocalTimezone(values.startDate),
                      value
                    )
                  );
                  setFieldValue(
                    'endDate',
                    calculateEndDateFromGracePeriod(
                      momentLocalTimezone(values.startDate),
                      values.term,
                      value,
                      values.isFirstUpfrontRentModality
                    )
                  );
                }}
                error={Boolean(touched.gracePeriod && errors.gracePeriod)}
                errorText={touched.gracePeriod && t(errors.gracePeriod)}
                disabled={shouldDisableGracePeriod(
                  data.enableMsi,
                  data.initialPayment,
                  data.isFirstUpfrontRentModality
                )}
              />
              <Autocomplete
                options={generateNumbersList(1, 25)}
                id="payday"
                label={t('NAVIGATION.PAY_DAY')}
                value={values.payday}
                fullWidth
                getOptionLabel={option => String(option)}
                onChange={(_, newValue) => {
                  setFieldValue('payday', newValue);
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    label={t('NAVIGATION.PAY_DAY')}
                    margin="normal"
                    fullWidth
                    error={Boolean(touched.payday && errors.payday)}
                    helperText={touched.payday && t(errors.payday)}
                  />
                )}
                sx={{ marginBottom: 1, marginTop: 2 }}
              />
              <DatePicker
                label={t('CONTRACT_VIEW.LAST_RENT_DATE')}
                value={values.endDate}
                disabled
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    sx: { width: '100%', margin: '16px 0' },
                    error: Boolean(touched.endDate && errors.endDate),
                    helperText: touched.endDate && t(errors.endDate),
                    FormHelperTextProps: { sx: { color: 'red' } },
                    InputProps: { autoComplete: 'off' }
                  }
                }}
              />
            </Box>

            <Box
              className={classes.box}
              display="flex"
              justifyContent="space-between"
            >
              <Button
                className={classes.buttons}
                onClick={() => handleBack()}
                color="primary"
                variant="outlined"
              >
                {t('COMMON.BACK')}
              </Button>
              <Button
                className={clsx(classes.buttons, classes.buttonMain)}
                type="submit"
                color="primary"
                variant="outlined"
              >
                {t('COMMON.NEXT')}
              </Button>
            </Box>
          </form>
        );
      }}
    </Formik>
  );
};
StepFour.propTypes = {
  data: PropTypes.object,
  handleBack: PropTypes.func,
  handleNext: PropTypes.func,
  resetRef: PropTypes.object
};

export default StepFour;
